import React, { useEffect, useState } from 'react';
import Word from 'saur_ui_library/src/Atoms/Word/Word';
import {
  VictoryAxis,
  VictoryBar,
  VictoryBrushContainer,
  VictoryChart,
  VictoryGroup,
  VictoryLegend,
  VictoryLine,
  VictoryScatter,
  VictoryTooltip,
  VictoryZoomContainer,
} from 'victory';
import {
  addOneDay,
  BlackOverlay,
  CustomTooltipForLevels,
  dataFormated,
  defineXPositionOfLegend,
  getTresholdsFormated,
  renderLabelsOnGraphics,
  testIfDatesAreEqual,
  tresholdsDataFormated,
  tresholdsTable,
  updateComponent,
} from '../../utils/helpers/view/brushAndZoomComponentHelper';
import Extend from '../../components/common/Icon/extend.svg';
import { getMaxValue } from '../../utils/helpers/view/chartsComponentHelper';

interface Props {
  data: any[];
  title: string;
  automatedTestId: string;
  pluvioData?: any[];
  piezoData?: any[];
  dataSelected?: any;
  tresholdsDataList?: any[];
  axisXLabel: string;
  axisYLabel: string;
  axisYLabelPluvio?: string;
  showHide?: string;
  handleShowHide?(pGraphicToGrow: string, pMode: string): void;
  legend?: any[];
  colorsForPompes?: string[];
  mode: string;
  zoomMode: string;
  typeOfGraphic?: any;
  handleZoomContainer?(pDomain: any): void;
  zoomDomain?: any;
  handleDisplayPompesButtons?(): void;
}

export const BrushAndZoomComponent = (props: Props) => {
  const [data, setData] = useState<any>([]);
  const [pluvioData, setPluvioData] = useState<any>([]);
  const [piezoData, setPiezoData] = useState<any>([]);
  const [tresholds, setTresholds] = useState<any>([]);
  const [pluvioMax, setPluvioMax] = useState<any>();
  const [piezoMax, setPiezoMax] = useState<any>();
  const [dataMax, setDataMax] = useState<any>();
  const [height, setHeight] = useState<any>(470);
  const [width, setWidth] = useState<any>(700);
  const [colors, setColors] = useState<any>(props.colorsForPompes);
  const [zoomDomain, setZoomDomain] = useState<any>({
    x: [new Date(1990, 1, 1), new Date(2009, 1, 1)],
  });

  // victory line for pluvio data
  const pluvioBar = () => {
    return (
      <VictoryBar
        style={{
          data: {
            fill: () => {
              return '#85C1E9';
            },
          },
        }}
        data={pluvioData}
        barWidth={4}
        y={(datum) => (datum.value / pluvioMax) * (dataMax > 0 ? dataMax : pluvioMax)}
        x='time'
        // need those 3 props to activate popup (labelComponent, labels, events)
        labelComponent={<VictoryTooltip />}
        labels={({ datum }) => {
          return renderLabelsOnGraphics(datum);
        }}
        events={[
          {
            target: 'data',
            eventHandlers: {
              onMouseOver: () => {
                return [
                  {
                    target: 'data',
                    mutation: (props) => {
                      return {
                        style: Object.assign({}, props.style, {
                          fill: 'black',
                        }),
                      };
                    },
                  },
                  {
                    target: 'labels',
                    mutation: () => ({ active: true }),
                  },
                ];
              },
              onMouseOut: () => {
                return [
                  {
                    target: 'data',
                    mutation: () => {
                      return null;
                    },
                  },
                  {
                    target: 'labels',
                    mutation: () => ({ active: false }),
                  },
                ];
              },
            },
          },
        ]}
      />
    );
  };

  const piezoLine = () => {
    return (
      <VictoryLine
        style={{
          data: {
            stroke: () => {
              return '#9400D3';
            },
          },
        }}
        data={piezoData}
        y={(datum) => (datum.value / piezoMax) * (dataMax > 0 ? dataMax : piezoMax)}
        x='time'
      />
    );
  };

  const piezoPoints = () => {
    return (
      ChildPoint({
        x: 'time',
        y:(datum) => (datum.value / piezoMax) * (dataMax > 0 ? dataMax : piezoMax),
        style: {
          data: { fill: 'transparent' },
        },
        data: piezoData,
        events: BlackOverlay,
        // need those 3 props to activate popup (labelComponent, labels, events)
        labelComponent: <VictoryTooltip />,
        labels: ({ datum }) => {
          return renderLabelsOnGraphics(datum);
        }
      })
    );
  };

  // mutual properties for graphics
  const mutualProperties = {
    x: 'time',
    y: 'value',
    labelComponent: <VictoryTooltip />,
    labels: ({ datum }) => {
      return renderLabelsOnGraphics(datum);
    },
  };

  // child for graphic class
  const ChildLine = (lineProperties: any) => {
    return <VictoryLine {...lineProperties} />;
  };

  const ChildBar = (barProperties: any) => {
    return <VictoryBar {...barProperties} />;
  };

  const ChildPoint = (lineProperties: any) => {
    return <VictoryScatter {...lineProperties} />;
  };

  const getColorization = (pFilter: string) => {
    return props.title !== 'Niveaux d\'eau du PR' ? colors : props.legend?.filter((l) => l.name.includes(pFilter) && l.symbol.fill).map((l) => l.symbol.fill);
  };

  const drawPointsForPopup = () => {
    const colorization = getColorization('Niveau');
    return (
      data.map((v, key) => {
        return colorization[key] !== 'transparent' && props.typeOfGraphic === 'line'
          && ChildPoint({
              x: 'time',
              y: 'value',
              key: key,
              style: {
                data: { fill: 'transparent' },
              },
              data: v,
              events: colorization[key] !== 'transparent' ? BlackOverlay : [],
              labelComponent: <VictoryTooltip flyoutComponent={<CustomTooltipForLevels {... props} pData={data}/>} />,
              // desable labels to avoid duplicates
              labels: () => '',
            });
      })
    );
  };

  // render of graphics hence props
  const drawData = () => {
    const colorization = getColorization('Niveau');
    return props.mode === 'solo'
      ? props.typeOfGraphic === 'line'
        ? ChildLine({
            ...mutualProperties,
            style: {
              data: { stroke: '#85929E' },
            },
            data: data,
          })
        : ChildBar({
            ...mutualProperties,
            style: {
              data: { fill: '#85929E' },
            },
            barWidth: 4,
            events: BlackOverlay,
            data: data,
          })
      : data.map((v, key) => {
          return colorization[key] !== 'transparent' && props.typeOfGraphic === 'line'
            ? ChildLine({
                ...mutualProperties,
                key: key,
                style: {
                  data: { stroke: colorization[key] },
                },
                data: v,
              })
            : ChildBar({
                ...mutualProperties,
                key: key,
                style: {
                  data: { fill: colorization[key] },
                },
                barWidth: 4,
                data: v,
                events: colorization[key] !== 'transparent' ? BlackOverlay : [],
              });
        });
  };

  const renderTresholds = (pElement: any, pFillCode: string, pId: number) => {
    return ChildLine({
      x: 'time',
      y: 'value',
      key: pId,
      data: pElement,
      standalone: false,
      style: {
        data: {
          stroke: pFillCode,
          strokeDasharray: 8,
        },
      },
    });
  };

  // victory line for tresholds data
  const tresholdsLines = () => {
    const dataTresholds = getTresholdsFormated(props.mode, tresholds);
    const colorization = getColorization('Seuil');
    return props.mode === 'solo' && props.tresholdsDataList
      ? dataTresholds.map((element: any, id: any) => {
          return element.map((el: any) => {
            return renderTresholds(el, 'red', id);
          });
        })
      : dataTresholds.map((element: any, id: number) => {
          return element.map((el: any, elId: number) => {
            return (
              colorization[props.title !== 'Niveaux d\'eau du PR' ? id : elId] !== 'transparent' &&
              renderTresholds(el, colorization[props.title !== 'Niveaux d\'eau du PR' ? id : elId], props.title !== 'Niveaux d\'eau du PR' ? id : elId)
            );
          });
        });
  };

  const handlerClick = () => {
    if (width === 700) {
      setWidth(width * 1.3);
      setHeight(height * 1.3);
      props.handleShowHide &&
        props.handleShowHide(props.automatedTestId, 'hide');
    } else {
      setWidth(700);
      setHeight(470);
      props.handleShowHide &&
        props.handleShowHide(props.automatedTestId, 'show');
    }
    // hide pompes buttons for graphic volume and pr rm module
    props.handleDisplayPompesButtons && props.handleDisplayPompesButtons();
  };

  // button for zoom in and zoom out on a graphic
  const zoomButton = () => {
    return (
      <div style={{ textAlign: 'end' }}>
        <img
          id='graphic-extend'
          alt='Agrandir/réduire le graphique'
          title='Agrandir/réduire le graphique'
          src={Extend}
          style={{
            cursor: width === 700 ? 'zoom-in' : 'zoom-out',
            margin: '5px',
          }}
          onClick={handlerClick}
          onKeyDown={handlerClick}
        ></img>
      </div>
    );
  };

  // handler of zooming
  const handleZoomComponent = (domain) => {
    setZoomDomain(domain);
  };

  const handleZoomContainer = (dataUpdated: any[]) => {
    if(updateComponent(props.title, props.data) === true){
      const newDomain = {
        x: [
          dataUpdated[0][0].time,
          testIfDatesAreEqual(
            dataUpdated[0][0].time.toString(),
            dataUpdated[0][dataUpdated[0].length - 1].time.toString()
          ) === false
            ? dataUpdated[0][dataUpdated[0].length - 1].time
            : addOneDay(dataUpdated[0][0].time),
        ],
      };
      if (props.zoomMode === 'synchro') {
        props.handleZoomContainer && props.handleZoomContainer(newDomain);
      } else {
        setZoomDomain(newDomain);
      }
    }
  };

  // handle change for props.data
  useEffect(() => {
    // init and format data
    if (updateComponent(props.title, props.data) === true) {
      // formated data
      if (props.mode === 'multiple') {
        let dataUpdated: any[] = [];
        props.data.forEach((d) => {
          dataUpdated && dataUpdated.push(dataFormated(d.measures !== undefined ? d.measures : d));
        });
        let dataMaxValue: number[] = [];
        dataUpdated.forEach((values) => {
          dataMaxValue &&
            dataMaxValue.push(
              Number(getMaxValue(values, 'value', 'positive').toPrecision(2))
            );
        });
        if (
          props.tresholdsDataList !== undefined &&
          props.tresholdsDataList.length !== 0
        ) {
          let tresholdUpdated: any[] = [];
          if(props.title !== 'Niveaux d\'eau du PR'){
            props.tresholdsDataList.forEach((d) => {
              tresholdUpdated &&
                tresholdUpdated.push(tresholdsDataFormated(d.tresholds));
            });
          } else {
            tresholdUpdated = props.tresholdsDataList.map((d) => tresholdsDataFormated(d));
          }
          setTresholds(tresholdUpdated);
        }
        setData(dataUpdated);
        setDataMax(Math.max(...dataMaxValue));
        handleZoomContainer(dataUpdated);
      } else {
        const dataUpdated = dataFormated(props.data);
        const dataMaxValue = getMaxValue(dataUpdated, 'value', 'positive').toPrecision(2);
        if (
          props.tresholdsDataList !== undefined &&
          props.tresholdsDataList.length !== 0
        ) {
          const tresholdsDataListUpdated = tresholdsDataFormated(
            props.tresholdsDataList
          );
          setTresholds([tresholdsDataListUpdated]);
        }
        setData(dataUpdated);
        setDataMax(dataMaxValue);
        handleZoomContainer([dataUpdated]);
      }
    }
  }, [props.data]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if(props.colorsForPompes !== undefined){
      setColors(props.colorsForPompes);
    }
  }, [props.colorsForPompes]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (props.piezoData !== undefined && props.pluvioData !== null && props.piezoData.length !== 0) {
      const piezoDataUpdated = dataFormated(props.piezoData);
      setPiezoData(piezoDataUpdated);
      setPiezoMax(getMaxValue(piezoDataUpdated, 'value', 'negative').toPrecision(2));
    } else {
      setPiezoData([]);
      setPiezoMax(0);
    }
  }, [props.piezoData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // init pluvio data
    if (props.pluvioData !== undefined && props.pluvioData !== null && props.pluvioData.length !== 0) {
      const pluvioDataUpdated = dataFormated(props.pluvioData);
      setPluvioData(pluvioDataUpdated);
      setPluvioMax(getMaxValue(pluvioDataUpdated, 'value', 'positive').toPrecision(2));
    } else {
      setPluvioData([]);
      setPluvioMax(0);
    }
  }, [props.pluvioData]); // eslint-disable-line react-hooks/exhaustive-deps

  // zoom feature
  const getZoomDomain = props.zoomMode === 'alone' ? zoomDomain : props.zoomDomain;
  const gethandleZoomContainer = props.zoomMode === 'alone' ? handleZoomComponent : props.handleZoomContainer;

  return (
    <div
      id={props.automatedTestId}
      style={
        {
          border: '1px solid rgb(237, 242, 250)',
          margin: '5px',
          padding: '5px',
          borderRadius: '10px',
          display: props.showHide,
          backgroundColor: 'white',
          boxShadow: 'rgba(149, 157, 165, 0.2) 0px 8px 24px',
        }
      }
    >
      {data.length !== 0 && <>
        <div
          style={{
            display: 'inline-flex',
            width: '100%',
            justifyContent: tresholds.length > 0 ? 'space-between' : 'end',
            marginBottom: '2px',
          }}
        >
          {/* header of graphic */}
          {tresholds.length > 0 && tresholdsTable(props.title, props.legend!, colors, tresholds)}
          {data.length !== 0 && zoomButton()}
        </div>
        <span style={{ textAlign: 'center' }}>
          <Word
            textStyle='titleSmall'
            automatedTestId={props.automatedTestId}
            variant='h1'
            value={props.title}
          />
        </span>
      </>}
      {/* content graphic */}
      {data.length !== 0 && (
        <>
          <div id='grahic' style={{ width: width, height: height }}>
            <VictoryChart
              width={700}
              height={470}
              scale={{ x: 'time' }}
              containerComponent={
                <VictoryZoomContainer
                  zoomDimension='x'
                  zoomDomain={getZoomDomain}
                  onZoomDomainChange={gethandleZoomContainer}
                />
              }
            >
              {props.legend !== undefined && (
                <VictoryLegend
                  x={defineXPositionOfLegend(props.title)}
                  y={props.title === 'Niveaux d\'eau du PR' ? -8 : 10}
                  centerTitle
                  titleOrientation='left'
                  orientation='horizontal'
                  gutter={20}
                  style={{ title: { fontSize: 15 } }}
                  data={props.legend?.filter((l) => l.show === true)}
                  itemsPerRow={props.title === 'Niveaux d\'eau du PR' ? 4 : 0}
                />
              )}
              {/* Measures */}
              {/* y axis */}
              <VictoryAxis
                dependentAxis
                standalone={false}
                style={{
                  axis: {
                    stroke: 'grey',
                    strokeWidth: 2,
                  },
                  axisLabel: {
                    padding: 35,
                  },
                  grid: {
                    stroke: '#818e99',
                    strokeWidth: 0.5,
                    strokeDasharray: '2,4',
                  },
                  tickLabels: {
                    fill: '#232323',
                    fontWeight: 400,
                    fontSize: 12,
                  },
                }}
                label={props.axisYLabel}
                tickFormat={(t) => `${t}`}
              />
              {/* x axis */}
              <VictoryAxis
                standalone={false}
                label={props.axisXLabel}
                style={{
                  axis: {
                    strokeWidth: 1,
                  },
                  axisLabel: {
                    padding: 30,
                  },
                  ticks: { stroke: 'grey', size: 5 },
                }}
              />

              {/* Pluviometry */}
              {/* y axis */}
              {props.dataSelected.id !== 3 && (pluvioData.length > 0 || piezoData.length > 0) && (
                <VictoryAxis
                  dependentAxis
                  standalone={false}
                  offsetX={650}
                  style={{
                    axis: {
                      stroke: 'grey',
                      strokeWidth: 2,
                    },
                    axisLabel: {
                      padding: -45,
                    },
                    grid: {
                      stroke: '#818e99',
                      strokeWidth: 0.5,
                      strokeDasharray: '2,4',
                    },
                    tickLabels: {
                      fill: '#006400',
                      fontWeight: 400,
                      fontSize: 12,
                      padding: -30,
                    },
                  }}
                  tickFormat={(t) =>
                    Number((t * (props.dataSelected.id === 1 ? pluvioMax : piezoMax)) / (dataMax > 0 ? dataMax : (props.dataSelected.id === 1 ? pluvioMax : piezoMax))).toPrecision(2)
                  }
                  label={props.axisYLabelPluvio}
                />
              )}

              {/* Line chart with tresholds */}
              {tresholdsLines()}
              <VictoryGroup offset={props.typeOfGraphic === 'bar' ? 6 : 0} style={{ data: { width: 6 } }}>
                {/* pluvio */}
                {props.dataSelected.id === 1 &&
                  pluvioData.length > 0 &&
                  pluvioBar()}
                {props.dataSelected.id === 2 &&
                  piezoData.length > 0 &&
                  piezoLine()}
                {props.dataSelected.id === 2 &&
                  piezoData.length > 0 &&
                  piezoPoints()}
                {/* main data */}
                {data.length !== 0 && drawData()}
                {/* points for popup : do not use createContainer because is not possible to desabled treshold popup 
                and you get duplicates popup on every graphics with black events */}
                {props.title === 'Niveaux d\'eau du PR' && data.length !== 0 && drawPointsForPopup()}
              </VictoryGroup>
            </VictoryChart>
          </div>
          <div>
            {/* little graphic */}
            <VictoryChart
              padding={{ top: 0, left: 50, right: 50, bottom: 30 }}
              width={700}
              height={100}
              scale={{ x: 'time' }}
              containerComponent={
                <VictoryBrushContainer
                  brushDimension='x'
                  brushDomain={getZoomDomain}
                  onBrushDomainChange={gethandleZoomContainer}
                />
              }
            >
              <VictoryAxis tickFormat={(x) => new Date(x).getFullYear()} />
              {/* Line chart with tresholds */}
              {tresholdsLines()}
              <VictoryGroup offset={6} style={{ data: { width: 6 } }}>
                {/* pluvio */}
                {props.dataSelected.id === 1 &&
                  pluvioData.length > 0 &&
                  pluvioBar()}
                {props.dataSelected.id === 2 &&
                  piezoData.length > 0 &&
                  piezoLine()}
                {/* main data */}
                {data.length !== 0 && drawData()}
              </VictoryGroup>
            </VictoryChart>
          </div>
        </>
      )}
      {data.length === 0 && 
        <div style={{
          width: '74vh',
          height: '69vh',
          textAlign: 'center',
          backgroundColor: 'white',
          display: 'grid',
          borderRadius: '10px',
        }}>
          <br />
          <Word
            textStyle='normalMed'
            automatedTestId='dataNombre-message-chart'
            variant='span'
            value={'Aucune données !'}
          />
        </div>
      }
    </div>
  );
};

export default React.memo(BrushAndZoomComponent);
